import { useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { Toaster, default as toast } from 'react-hot-toast'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import Quiosco from './pages/Quiosco'
import Login from './pages/Login'
import Registro from './pages/Registro'
import LandingPage from './pages/LandingPageV2'
import DashboardV2 from './pages/v2/Dashboard'
import AppLayoutV2 from './pages/v2/AppLayoutV2'
import ReportesFichajesV2 from './pages/v2/ReportesFichajes'
import MiFichajeV2 from './pages/v2/MiFichaje'
import HistorialFichajesV2 from './pages/v2/HistorialFichajes'
import MisAusenciasV2 from './pages/v2/MisAusencias'
import GestionFichajesV2 from './pages/v2/GestionFichajes'
import MisHorasExtrasV2 from './pages/v2/MisHorasExtras'
import CalendarioVacacionesV2 from './pages/v2/CalendarioVacaciones'
import FichajeGrupalV2 from './pages/v2/FichajeGrupal'
import MapaFichajesV2 from './pages/v2/MapaFichajes'
import MisDatosV2 from './pages/v2/MisDatos'
import BlogRegistroHorario from './pages/BlogRegistroHorario'
import Blog from './pages/Blog'
import SancionesNoFichar from './pages/blog/SancionesNoFichar'
import ComoElegirSistemaFichaje from './pages/blog/ComoElegirSistemaFichaje'
import GeolocalizacionFichajes from './pages/blog/GeolocalizacionFichajes'
import PlantillaExcelControlHorario from './pages/blog/PlantillaExcelControlHorario'
import InspeccionTrabajoControlHorario from './pages/blog/InspeccionTrabajoControlHorario'
import DiferenciaControlHorarioRegistroJornada from './pages/blog/DiferenciaControlHorarioRegistroJornada'
import RealDecreto2026FichajeDigital from './pages/blog/RealDecreto2026FichajeDigital'
import FichajeTeletrabajoRemoto from './pages/blog/FichajeTeletrabajoRemoto'
import AsesoriasControlHorario from './pages/blog/AsesoriasControlHorario'
import BancaHorasVsHorasExtras from './pages/blog/BancaHorasVsHorasExtras'
import NewsletterConfirmar from './pages/blog/NewsletterConfirmar'
import BlogPost from './pages/blog/BlogPost'
import BlogAdmin from './pages/admin/BlogAdmin'
import Privacidad from './pages/Privacidad'
import AvisoLegal from './pages/AvisoLegal'
import Cookies from './pages/Cookies'
import ControlHorarioEmpresas from './pages/ControlHorarioEmpresas'
import FichajeDigital from './pages/FichajeDigital'
import Precios from './pages/Precios'
import BillingSuccess from './pages/BillingSuccess'
import BillingCancel from './pages/BillingCancel'
import GestionFichajes from './pages/GestionFichajes'
import ReportesFichajes from './pages/ReportesFichajes'
import ReporteAuditoria from './pages/ReporteAuditoria'
import GestionEmpresas from './pages/GestionEmpresas'
import GestionUsuariosGlobal from './pages/GestionUsuariosGlobal'
import GestionLeads from './pages/GestionLeads'
import GestionEmpleados from './pages/GestionEmpleados'
import ConfiguracionEmpresa from './pages/ConfiguracionEmpresa'
import GestionEmpresaAdmin from './pages/v2/GestionEmpresaAdmin'
import GestionClientesAsesoria from './pages/GestionClientesAsesoria'
import GestionGestoresAsesoria from './pages/GestionGestoresAsesoria'
import GestionEmpresaAsesoria from './pages/v2/GestionEmpresaAsesoria'
import MisClientes from './pages/MisClientes'
import GestionCentrosTrabajo from './pages/GestionCentrosTrabajo'
import GestionCalendarios from './pages/GestionCalendarios'
import GestionGrupos from './pages/GestionGrupos'
import GestionTiposPausa from './pages/GestionTiposPausa'
import GestionTiposAusencia from './pages/GestionTiposAusencia'
import UpgradePlanPrompt from './components/UpgradePlanPrompt'
import TrialExpiradoScreen from './components/TrialExpiradoScreen'
import CookieBanner from './components/CookieBanner'
import Suscripcion from './pages/Suscripcion'
import SolicitudesHorasExtras from './pages/SolicitudesHorasExtras'
import GestionSistema from './pages/GestionSistema'
import { canAccess, getEffectivePlan } from './config/planFeatures'
import { useEmpresaGestion } from './contexts/EmpresaGestionContext'

// Guardia de plan: muestra UpgradePlanPrompt si la feature no está disponible
function PlanGuard({ feature, children }) {
  const { empresaInfo, empleado } = useAuth()
  if (empleado?.rol === 'super_admin') return children
  const plan = getEffectivePlan(empresaInfo)
  if (canAccess(plan, feature)) return children
  return <UpgradePlanPrompt />
}

// Componente de ruta protegida
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Cargando...</div>
      </div>
    )
  }

  return isAuthenticated ? children : <Navigate to="/landing" replace />
}

// Guardia de suscripción: bloquea acceso cuando trial/suscripción expirada
// Permite: reportes, historial, suscripción, billing
function SubscriptionGuard({ children, allowWhenExpired }) {
  const { empleado, trialExpirado, empresaBloqueada } = useAuth()
  if (empleado?.rol === 'super_admin') return children
  if (allowWhenExpired) return children
  if (trialExpirado || empresaBloqueada) return <TrialExpiradoScreen />
  return children
}

// En /admin/empresa/:id/empleados: muestra gestores si es asesoría, empleados si es cliente normal
function EmpleadosRoute() {
  const { empresa } = useEmpresaGestion()
  if (empresa?.tipo === 'asesoria') return <GestionGestoresAsesoria />
  return <GestionEmpleados />
}

// En ruta principal /empleados: gestores para asesoría, empleados para el resto
function EmpleadosMainRoute() {
  const { isAsesoria } = useAuth()
  if (isAsesoria) return <GestionGestoresAsesoria />
  return <GestionEmpleados />
}

// Redirect para /v2 según tipo de cuenta. La home legacy (HomeRedirect) ya no
// existe — todas las rutas raíz redirigen a /v2.
function V2HomeRedirect() {
  const { empleado, isAsesoria } = useAuth()
  if (empleado?.rol === 'super_admin') return <Navigate to="/v2/empresas" replace />
  if (empleado?.rol === 'admin' && isAsesoria) return <Navigate to="/v2/mis-clientes" replace />
  return <MiFichajeV2 />
}

// KioskGuard — si el flag `kiosk_mode` está activo en localStorage, fuerza la
// ruta /quiosco desde cualquier otra. Evita que un empleado salga del modo
// quiosco simplemente cambiando la URL. La única salida legítima es el
// long-press del logo en la propia pantalla del quiosco (DesactivarQuioscoModal),
// que limpia el flag y hace location.replace('/login').
function KioskGuard({ children }) {
  const location = useLocation()
  const navigate = useNavigate()
  useEffect(() => {
    const kioskMode = localStorage.getItem('kiosk_mode') === 'true'
    if (kioskMode && location.pathname !== '/quiosco') {
      navigate('/quiosco', { replace: true })
    }
  }, [location.pathname, navigate])
  return children
}

// Avisa cuando el backend rechaza una petición por plan insuficiente.
// El interceptor de services/api.js emitía este evento desde el principio pero
// nadie lo escuchaba: los 403 acababan como un toast genérico o se perdían en
// silencio, y el usuario veía pantallas vacías sin saber que le faltaba plan.
// Se agrupa por feature para no encadenar toasts cuando varias llamadas de la
// misma pantalla fallan a la vez.
function PlanInsuficienteAviso() {
  useEffect(() => {
    let ultimoAviso = 0
    const handler = () => {
      const ahora = Date.now()
      if (ahora - ultimoAviso < 5000) return
      ultimoAviso = ahora
      toast.error('Esta funcionalidad requiere el plan Avanzado.', { id: 'plan-insuficiente' })
    }
    window.addEventListener('plan-insuficiente', handler)
    return () => window.removeEventListener('plan-insuficiente', handler)
  }, [])
  return null
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <Toaster position="top-right" />
        <PlanInsuficienteAviso />
        <CookieBanner />
        <KioskGuard>
        <Routes>
          {/* Rutas públicas */}
          {/* Modo Quiosco — pública por diseño: no requiere JWT, autentica
              cada petición por PIN+empresa_id contra /api/quiosco/*. La
              KioskGuard fuerza a esta ruta cuando localStorage.kiosk_mode
              está activo desde cualquier otra URL. */}
          <Route path="/quiosco" element={<Quiosco />} />
          <Route path="/landing" element={<LandingPage />} />
          {/* Alias legacy: /landing-v2 → /landing (la v2 ya es la canónica) */}
          <Route path="/landing-v2" element={<Navigate to="/landing" replace />} />
          {/* App v2 demo — ruta oculta para validar AppShellV2 antes de integrarlo en Fichaje.jsx */}
          <Route path="/app-v2/dashboard" element={<DashboardV2 />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/newsletter/confirmar" element={<NewsletterConfirmar />} />
          <Route path="/blog/registro-horario-obligatorio-espana" element={<BlogRegistroHorario />} />
          <Route path="/blog/sanciones-no-fichar-2026" element={<SancionesNoFichar />} />
          <Route path="/blog/como-elegir-sistema-fichaje" element={<ComoElegirSistemaFichaje />} />
          <Route path="/blog/geolocalizacion-fichajes-rgpd" element={<GeolocalizacionFichajes />} />
          <Route path="/blog/plantilla-excel-control-horario" element={<PlantillaExcelControlHorario />} />
          <Route path="/blog/inspeccion-trabajo-control-horario" element={<InspeccionTrabajoControlHorario />} />
          <Route path="/blog/diferencia-control-horario-registro-jornada" element={<DiferenciaControlHorarioRegistroJornada />} />
          <Route path="/blog/real-decreto-2026-fichaje-digital" element={<RealDecreto2026FichajeDigital />} />
          <Route path="/blog/fichaje-teletrabajo-remoto" element={<FichajeTeletrabajoRemoto />} />
          <Route path="/blog/asesorias-control-horario-clientes" element={<AsesoriasControlHorario />} />
          <Route path="/blog/banca-horas-vs-horas-extras" element={<BancaHorasVsHorasExtras />} />
          {/* Catch-all dinámico del blog: hace fetch a /api/blog/posts/:slug.
              Va al final del bloque /blog/* — React Router 6 prefiere matches
              estáticos sobre dinámicos, así que las rutas hardcoded de arriba
              ganan hasta que se migren a BD en Fase 2E. */}
          <Route path="/blog/:slug" element={<BlogPost />} />
          {/* Admin del blog (super_admin only — verificación dentro del componente). */}
          <Route path="/admin/blog" element={<ProtectedRoute><BlogAdmin /></ProtectedRoute>} />
          <Route path="/privacidad" element={<Privacidad />} />
          <Route path="/aviso-legal" element={<AvisoLegal />} />
          <Route path="/cookies" element={<Cookies />} />
          <Route path="/control-horario-empresas" element={<ControlHorarioEmpresas />} />
          <Route path="/fichaje-digital" element={<FichajeDigital />} />
          <Route path="/precios" element={<Precios />} />
          <Route path="/login" element={<Login />} />
          <Route path="/registro" element={<Registro />} />
          <Route path="/billing/success" element={<ProtectedRoute><BillingSuccess /></ProtectedRoute>} />
          <Route path="/billing/cancel" element={<ProtectedRoute><BillingCancel /></ProtectedRoute>} />

          {/* Rutas de administración de empresa específica (Super Admin) - Fuera del layout */}
          <Route
            path="/admin/empresa/:empresaId"
            element={
              <ProtectedRoute>
                <GestionEmpresaAdmin />
              </ProtectedRoute>
            }
          >
            {/* El index redirect lo gestiona GestionEmpresaAdmin según el tipo */}
            <Route path="empleados" element={<EmpleadosRoute />} />
            <Route path="gestion" element={<GestionFichajes />} />
            <Route path="reportes" element={<ReportesFichajes />} />
            <Route path="auditoria" element={<ReporteAuditoria />} />
            <Route path="configuracion" element={<ConfiguracionEmpresa />} />
            <Route path="clientes" element={<GestionClientesAsesoria />} />
            <Route path="centros-trabajo" element={<GestionCentrosTrabajo />} />
            <Route path="calendarios" element={<GestionCalendarios />} />
            <Route path="grupos" element={<GestionGrupos />} />
            <Route path="tipos-pausa" element={<GestionTiposPausa />} />
            <Route path="tipos-ausencia" element={<GestionTiposAusencia />} />
          </Route>

          {/* Rutas de gestión para asesorías */}
          <Route
            path="/asesoria/empresa/:empresaId"
            element={<ProtectedRoute><GestionEmpresaAsesoria /></ProtectedRoute>}
          >
            <Route index element={<Navigate to="empleados" replace />} />
            <Route path="empleados"     element={<GestionEmpleados />} />
            <Route path="gestion"       element={<GestionFichajes />} />
            <Route path="reportes"      element={<ReportesFichajes />} />
            <Route path="auditoria"     element={<ReporteAuditoria />} />
            <Route path="configuracion" element={<ConfiguracionEmpresa />} />
            <Route path="centros-trabajo" element={<GestionCentrosTrabajo />} />
            <Route path="calendarios"   element={<GestionCalendarios />} />
            <Route path="grupos"        element={<GestionGrupos />} />
            <Route path="tipos-pausa"   element={<GestionTiposPausa />} />
            <Route path="tipos-ausencia" element={<GestionTiposAusencia />} />
          </Route>

          {/* App v2 — capa paralela con AppShellV2 + páginas REALES.
              Replica las rutas de Fichaje pero bajo /v2 para validar el rediseño
              sin tocar la app de producción. Misma cadena de guards. */}
          <Route
            path="/v2"
            element={<ProtectedRoute><AppLayoutV2 /></ProtectedRoute>}
          >
            <Route index element={<SubscriptionGuard><V2HomeRedirect /></SubscriptionGuard>} />
            <Route path="historial" element={<SubscriptionGuard allowWhenExpired><HistorialFichajesV2 /></SubscriptionGuard>} />
            <Route path="ausencias" element={<SubscriptionGuard><PlanGuard feature="ausencias"><MisAusenciasV2 /></PlanGuard></SubscriptionGuard>} />
            <Route path="empleados" element={<SubscriptionGuard><EmpleadosMainRoute /></SubscriptionGuard>} />
            <Route path="gestion" element={<SubscriptionGuard><GestionFichajesV2 /></SubscriptionGuard>} />
            <Route path="reportes" element={<SubscriptionGuard allowWhenExpired><ReportesFichajesV2 /></SubscriptionGuard>} />
            <Route path="auditoria" element={<SubscriptionGuard allowWhenExpired><ReporteAuditoria /></SubscriptionGuard>} />
            <Route path="configuracion" element={<SubscriptionGuard><ConfiguracionEmpresa /></SubscriptionGuard>} />
            <Route path="centros-trabajo" element={<SubscriptionGuard><GestionCentrosTrabajo /></SubscriptionGuard>} />
            <Route path="calendarios" element={<SubscriptionGuard><GestionCalendarios /></SubscriptionGuard>} />
            <Route path="grupos" element={<SubscriptionGuard><GestionGrupos /></SubscriptionGuard>} />
            <Route path="tipos-pausa" element={<SubscriptionGuard><GestionTiposPausa /></SubscriptionGuard>} />
            <Route path="tipos-ausencia" element={<SubscriptionGuard><PlanGuard feature="tipos_ausencia"><GestionTiposAusencia /></PlanGuard></SubscriptionGuard>} />
            <Route path="empresas" element={<GestionEmpresas />} />
            <Route path="usuarios-global" element={<GestionUsuariosGlobal />} />
            <Route path="horas-extras" element={<SubscriptionGuard><SolicitudesHorasExtras /></SubscriptionGuard>} />
            <Route path="mis-horas-extras" element={<SubscriptionGuard><MisHorasExtrasV2 /></SubscriptionGuard>} />
            <Route path="leads" element={<GestionLeads />} />
            <Route path="sistema" element={<GestionSistema />} />
            <Route path="mis-clientes" element={<MisClientes />} />
            <Route path="suscripcion" element={<Suscripcion />} />
            <Route path="fichaje-grupal" element={<SubscriptionGuard><FichajeGrupalV2 /></SubscriptionGuard>} />
            <Route path="calendario-vacaciones" element={<SubscriptionGuard><PlanGuard feature="ausencias"><CalendarioVacacionesV2 /></PlanGuard></SubscriptionGuard>} />
            <Route path="geolocalizacion" element={<SubscriptionGuard><PlanGuard feature="geolocalizacion"><MapaFichajesV2 /></PlanGuard></SubscriptionGuard>} />
            <Route path="mis-datos" element={<SubscriptionGuard allowWhenExpired><MisDatosV2 /></SubscriptionGuard>} />
          </Route>

          {/* Rutas legacy → redirigen a /v2 (mantenidas por compatibilidad de bookmarks).
              Las páginas de gestión siguen importándose porque se usan dentro de
              /admin/empresa/:id/* y /asesoria/empresa/:id/*, que tienen su propio shell. */}
          <Route path="/" element={<ProtectedRoute><Navigate to="/v2" replace /></ProtectedRoute>} />
          <Route path="/historial" element={<Navigate to="/v2/historial" replace />} />
          <Route path="/ausencias" element={<Navigate to="/v2/ausencias" replace />} />
          <Route path="/empleados" element={<Navigate to="/v2/empleados" replace />} />
          <Route path="/gestion" element={<Navigate to="/v2/gestion" replace />} />
          <Route path="/reportes" element={<Navigate to="/v2/reportes" replace />} />
          <Route path="/auditoria" element={<Navigate to="/v2/auditoria" replace />} />
          <Route path="/configuracion" element={<Navigate to="/v2/configuracion" replace />} />
          <Route path="/centros-trabajo" element={<Navigate to="/v2/centros-trabajo" replace />} />
          <Route path="/calendarios" element={<Navigate to="/v2/calendarios" replace />} />
          <Route path="/grupos" element={<Navigate to="/v2/grupos" replace />} />
          <Route path="/tipos-pausa" element={<Navigate to="/v2/tipos-pausa" replace />} />
          <Route path="/tipos-ausencia" element={<Navigate to="/v2/tipos-ausencia" replace />} />
          <Route path="/empresas" element={<Navigate to="/v2/empresas" replace />} />
          <Route path="/usuarios-global" element={<Navigate to="/v2/usuarios-global" replace />} />
          <Route path="/horas-extras" element={<Navigate to="/v2/horas-extras" replace />} />
          <Route path="/mis-horas-extras" element={<Navigate to="/v2/mis-horas-extras" replace />} />
          <Route path="/leads" element={<Navigate to="/v2/leads" replace />} />
          <Route path="/sistema" element={<Navigate to="/v2/sistema" replace />} />
          <Route path="/mis-clientes" element={<Navigate to="/v2/mis-clientes" replace />} />
          <Route path="/suscripcion" element={<Navigate to="/v2/suscripcion" replace />} />
          <Route path="/fichaje-grupal" element={<Navigate to="/v2/fichaje-grupal" replace />} />
          <Route path="/calendario-vacaciones" element={<Navigate to="/v2/calendario-vacaciones" replace />} />
          <Route path="/geolocalizacion" element={<Navigate to="/v2/geolocalizacion" replace />} />

          {/* Redirección por defecto */}
          <Route path="*" element={<Navigate to="/v2" replace />} />
        </Routes>
        </KioskGuard>
      </AuthProvider>
    </Router>
  )
}

export default App
